import { registerPlugin } from "@capacitor/core";

import { getPrivacyConsent, PRIVACY_CONSENT_EVENT } from "@/lib/privacyConsent";
import { getNativePlatform } from "@/lib/runtime";

const AnalyticsConsent = registerPlugin("AnalyticsConsent");
const measurementId = String(process.env.REACT_APP_GA_MEASUREMENT_ID || "").trim();
let webTagPromise = null;

function clearAnalyticsCookies() {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  const hostname = window.location.hostname;
  const hostnameParts = hostname.split(".").filter(Boolean);
  const parentDomain = hostnameParts.length > 2 && !/^\d+(?:\.\d+){3}$/.test(hostname)
    ? hostnameParts.slice(-2).join(".")
    : null;
  const domains = [null, hostname, `.${hostname}`];
  if (parentDomain && parentDomain !== hostname) {
    domains.push(parentDomain, `.${parentDomain}`);
  }

  document.cookie.split(";").forEach((part) => {
    const name = part.split("=")[0]?.trim();
    if (!name || (!name.startsWith("_ga") && name !== "_gid")) return;
    domains.forEach((domain) => {
      const domainAttribute = domain ? `; domain=${domain}` : "";
      document.cookie = `${name}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainAttribute}; SameSite=Lax`;
    });
  });
}

function sanitizeScreenPath(value) {
  const raw = String(value || "/");
  const pathname = raw.split(/[?#]/, 1)[0] || "/";
  return (pathname.startsWith("/") ? pathname : `/${pathname}`).slice(0, 100);
}

function ensureGtag() {
  if (!measurementId || typeof window === "undefined") return Promise.resolve(false);
  if (webTagPromise) return webTagPromise;

  webTagPromise = new Promise((resolve) => {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
    window.gtag("consent", "default", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    window.gtag("js", new Date());
    window.gtag("config", measurementId, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      anonymize_ip: true,
      cookie_domain: window.location.hostname,
    });

    const existing = document.querySelector(`script[data-lapstr-analytics="${measurementId}"]`);
    if (existing) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.dataset.lapstrAnalytics = measurementId;
    script.onload = () => resolve(true);
    script.onerror = () => {
      webTagPromise = null;
      resolve(false);
    };
    document.head.appendChild(script);
  });
  return webTagPromise;
}

export async function applyAnalyticsConsent(enabled) {
  if (getNativePlatform() === "android") {
    await AnalyticsConsent.setEnabled({ enabled: enabled === true });
    return;
  }

  if (!measurementId || typeof window === "undefined") return;
  window[`ga-disable-${measurementId}`] = enabled !== true;
  if (!enabled) {
    window.gtag?.("consent", "update", { analytics_storage: "denied" });
    clearAnalyticsCookies();
    return;
  }
  await ensureGtag();
  window[`ga-disable-${measurementId}`] = false;
  window.gtag?.("consent", "update", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
}

export async function trackScreenView(pathname) {
  const consent = getPrivacyConsent();
  if (!consent.analyticsDecided || !consent.analyticsEnabled) return;
  const screenName = sanitizeScreenPath(pathname);

  if (getNativePlatform() === "android") {
    await AnalyticsConsent.logScreen({ screenName });
    return;
  }
  if (!measurementId) return;
  const loaded = await ensureGtag();
  if (!loaded) return;
  window.gtag("event", "page_view", {
    page_path: screenName,
    page_location: `${window.location.origin}${screenName}`,
    page_title: document.title,
  });
}

export function subscribeToAnalyticsConsent(listener) {
  const handler = (event) => listener(event?.detail || getPrivacyConsent());
  window.addEventListener(PRIVACY_CONSENT_EVENT, handler);
  return () => window.removeEventListener(PRIVACY_CONSENT_EVENT, handler);
}
